import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import { Table, TableBody, TableCell, TableRow } from '@/shared/ui/table';
import { Tooltip } from '@/shared/ui/tooltip';

/** Grade de seções: uma coluna no celular, duas no tablet, três no desktop. */
export function FlagGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3', className)}>{children}</div>;
}

/** Um bloco do menu: título e as flags dele numa tabela própria. */
export function FlagSection({ title, children, wide }: { title: string; children: ReactNode; wide?: boolean }) {
  return (
    <section className={cn('overflow-hidden rounded-2xl border border-border/60 bg-card', wide && 'md:col-span-2 xl:col-span-3')}>
      <h4 className="border-b border-border/60 bg-muted/40 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h4>
      <Table>
        <TableBody>{children}</TableBody>
      </Table>
    </section>
  );
}

export function FlagLabel({ id, label, effect }: { id?: string; label: string; effect: string }) {
  return (
    <span className="flex min-w-0 items-center gap-1.5">
      <Label htmlFor={id} className="cursor-pointer truncate font-normal leading-normal">
        {label}
      </Label>
      <Tooltip content={effect} />
    </span>
  );
}

/** Linha com switch. `detail` abre uma linha abaixo (campos do recurso ligado). */
export function FlagRow({
  id,
  label,
  effect,
  checked,
  disabled,
  onCheckedChange,
  aside,
  detail,
}: {
  id: string;
  label: string;
  effect: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (next: boolean) => void;
  /** Texto curto abaixo do rótulo (estado, fila…). */
  aside?: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <>
      <TableRow>
        <TableCell className="py-2.5">
          <FlagLabel id={id} label={label} effect={effect} />
          {aside ? <p className="mt-0.5 text-xs text-muted-foreground">{aside}</p> : null}
        </TableCell>
        <TableCell className="w-14 py-2.5 text-right">
          <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onCheckedChange} />
        </TableCell>
      </TableRow>
      {detail ? (
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={2} className="bg-muted/20 py-3">
            {detail}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
}
